import type { components } from "@majitap/sdk";
import { Button } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarDays, MessageSquareText } from "lucide-react";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { dispatchActions, fillsiteOptionsQuery, fleetQueries } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { kmBetween, truckPositionsQuery } from "../../tracking";
import { useRefreshBoard } from "./actions";
import styles from "./assign-dialog.module.css";
import { RescheduleDialog } from "./reschedule-dialog";

type Row = components["schemas"]["DeliveryListItem"];

/** Deliveries a dispatcher can still plan: the dialog opens for these. */
export const PLANNABLE = new Set<Row["status"]>(["pending", "fillsite_assigned", "assigned"]);

const matches = (q: string, ...values: (string | null | undefined)[]) =>
  !q || values.some((v) => v?.toLowerCase().includes(q.toLowerCase()));

/**
 * Assign a delivery in one dialog (P8-77, `Console / Dispatch / Table / Assign`): any approved
 * fill site, nearest to the place first, and any driver on shift, nearest to that fill site
 * first, free or busy. A truck too small for the load can't be chosen (the API refuses it too).
 * The order's note and the scheduled time stay in view.
 */
export function AssignDialog({ row, onClose }: { row: Row; onClose: () => void }): ReactNode {
  const format = useCompanyFormat();
  const refresh = useRefreshBoard();
  const options = useQuery(fillsiteOptionsQuery(row.id));
  const fleetSites = useQuery(fleetQueries.fillsites);
  const shifts = useQuery(fleetQueries.shifts);
  const positions = useQuery(truckPositionsQuery);
  const [fillsiteId, setFillsiteId] = useState<string | null>(row.fillsite?.id ?? null);
  const [shiftId, setShiftId] = useState<string | null>(null);
  const [siteQuery, setSiteQuery] = useState("");
  const [driverQuery, setDriverQuery] = useState("");
  const [reason, setReason] = useState("");
  const [savedSite, setSavedSite] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  // every approved fill site: the nearest ones with their distance, then the rest by name
  const near = options.data?.data ?? [];
  const rest = (fleetSites.data ?? [])
    .filter((f) => !near.some((n) => n.id === f.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const sites = [
    ...near.map((s) => ({
      id: s.id,
      name: s.name,
      point: s,
      meta: [
        s.partner ? `Partner · ${s.partner.name}` : "Own site",
        s.distance_km !== null ? `${s.distance_km} km` : null,
      ],
    })),
    ...rest.map((s) => ({
      id: s.id,
      name: s.name,
      point: s,
      meta: [s.partner_id ? "Partner" : "Own site", null],
    })),
  ];
  const site = sites.find((s) => s.id === fillsiteId) ?? null;
  const nearestId = near[0]?.id;

  const positionOf = (truckId: string | undefined) => positions.data?.data.find((p) => p.truck_id === truckId);
  const litres = row.litres ?? 0;
  const drivers = (shifts.data ?? [])
    .filter((s) => s.truck)
    .map((s) => {
      const p = positionOf(s.truck?.id);
      const job = p?.delivery && p.delivery.id !== row.id ? p.delivery : null;
      return {
        shift: s,
        km: p && !p.stale && site ? kmBetween(p, site.point) : null,
        job,
        fits: litres === 0 || (s.truck?.capacity_litres ?? 0) >= litres,
      };
    })
    .sort(
      (a, b) =>
        (a.km ?? Number.POSITIVE_INFINITY) - (b.km ?? Number.POSITIVE_INFINITY) ||
        a.shift.driver.name.localeCompare(b.shift.driver.name),
    );
  const chosen = drivers.find((d) => d.shift.id === shiftId) ?? null;
  const changing = Boolean(row.driver || (row.fillsite && fillsiteId !== row.fillsite.id));

  const save = useMutation({
    mutationFn: async () => {
      const why = changing ? reason.trim() || undefined : undefined;
      // the fill site first: the driver is planned against it
      if (fillsiteId && fillsiteId !== row.fillsite?.id) {
        await dispatchActions.fillsite(row.id, fillsiteId, row.fillsite ? why : undefined);
        setSavedSite(true);
      }
      if (chosen) await dispatchActions.assign(row.id, { driver_id: chosen.shift.driver.id, reason: why });
    },
    onSettled: () => refresh(),
    onSuccess: onClose,
  });

  const scheduled = row.scheduled_at
    ? new Intl.DateTimeFormat("en-GB", {
        timeZone: format.timezone,
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(row.scheduled_at))
    : "As soon as possible";
  const title = `${row.driver ? "Reassign" : "Assign"} ${row.delivery_number}`;
  const description = [
    row.business?.name ?? row.customer.name,
    [row.location?.label, row.zone?.name].filter(Boolean).join(", "),
    row.litres ? `${row.litres.toLocaleString("en-GB")} L` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (rescheduling)
    return (
      <RescheduleDialog
        delivery={{
          id: row.id,
          delivery_number: row.delivery_number,
          scheduled_at: row.scheduled_at,
          customer: row.business?.name ?? row.customer.name,
          fillsite: row.fillsite?.name ?? null,
          driver: row.driver?.name ?? null,
        }}
        onDone={onClose}
        onClose={() => setRescheduling(false)}
      />
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} description={description} className={styles.dialog}>
        <div className={styles.facts}>
          <span className={styles.when}>
            <CalendarDays size={14} aria-hidden /> {scheduled} ·{" "}
            <button type="button" className={styles.reschedule} onClick={() => setRescheduling(true)}>
              Reschedule
            </button>
          </span>
          {row.note ? (
            <div className={styles.note}>
              <MessageSquareText size={16} aria-hidden />
              <div>
                <strong>Order note</strong>
                <p>{row.note}</p>
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.panes}>
          <section className={styles.pane} aria-label="Fill site">
            <h3 className={styles.paneTitle}>1 Fill site</h3>
            <p className={styles.hint}>Any approved fill site. Nearest to the place first.</p>
            <div className={styles.bare}>
              <SearchField
                label="Find a fill site"
                placeholder="Fill site"
                value={siteQuery}
                onValueChange={setSiteQuery}
              />
            </div>
            {options.isPending && fleetSites.isPending ? (
              <p className={styles.hint}>Loading the fill sites…</p>
            ) : sites.length === 0 ? (
              <p className={styles.hint}>No approved fill site yet.</p>
            ) : (
              <RadioCards
                layout="list"
                aria-label="Fill sites"
                value={fillsiteId}
                onValueChange={setFillsiteId}
                options={sites
                  .filter((s) => matches(siteQuery, s.name) || s.id === fillsiteId)
                  .map((s) => ({
                    value: s.id,
                    label: s.name,
                    description: [
                      ...s.meta,
                      s.id === row.fillsite?.id ? "Current" : s.id === nearestId ? "Nearest" : null,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                  }))}
              />
            )}
          </section>

          <section className={styles.pane} aria-label="Driver">
            <h3 className={styles.paneTitle}>2 Driver</h3>
            <p className={styles.hint}>
              Any driver on shift{site ? `, nearest to ${site.name} first` : ""}. A busy driver takes it after their
              current delivery.
            </p>
            <div className={styles.bare}>
              <SearchField
                label="Find a driver"
                placeholder="Driver or plate"
                value={driverQuery}
                onValueChange={setDriverQuery}
              />
            </div>
            {drivers.length === 0 ? (
              <p className={styles.hint}>Nobody is on shift. Drivers start a shift in the driver app.</p>
            ) : (
              <RadioCards
                layout="list"
                aria-label="Drivers on shift"
                value={shiftId}
                onValueChange={setShiftId}
                options={drivers
                  .filter(
                    (d) =>
                      matches(driverQuery, d.shift.driver.name, d.shift.truck?.plate_number) || d.shift.id === shiftId,
                  )
                  .map((d) => ({
                    value: d.shift.id,
                    label: d.shift.driver.name,
                    disabled: !d.fits,
                    disabledReason: `Truck too small: ${d.shift.truck?.plate_number} carries ${d.shift.truck?.capacity_litres.toLocaleString("en-GB")} L`,
                    description: [
                      d.job ? `On ${d.job.delivery_number}` : "Free",
                      d.shift.truck
                        ? `${d.shift.truck.plate_number} · ${d.shift.truck.capacity_litres.toLocaleString("en-GB")} L`
                        : null,
                      d.km !== null ? `${d.km} km` : null,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                  }))}
              />
            )}
          </section>
        </div>

        {changing ? (
          <Input
            label="Reason for the change (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Truck broke down"
            description="It goes on the delivery's timeline with the new fill site or driver."
          />
        ) : null}

        {save.error ? (
          <Alert tone="danger" title={savedSite ? "The fill site is saved, the driver isn't" : "Not assigned"}>
            {messageFor(errorOf(save.error))}
          </Alert>
        ) : null}

        <div className={styles.footer}>
          <span className={styles.summary}>
            {[
              site?.name ?? "No fill site yet",
              chosen ? `${chosen.shift.driver.name}, ${chosen.shift.truck?.plate_number}` : null,
            ]
              .filter(Boolean)
              .join(" → ")}
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!chosen && (!fillsiteId || fillsiteId === row.fillsite?.id)}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {chosen ? `${row.driver ? "Reassign to" : "Assign"} ${chosen.shift.driver.name}` : "Use this fill site"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
